import { useState, useEffect, useRef, useMemo } from "react";
import { apiGet } from "../../api";
import { CheckboxField } from "../checkbox";
import "./AssetTypesField.scss";

const GATHER_FACTS_KEY = "gather_facts";
const GATHER_FACTS_OPTIONS = [
    "!all",
    "!min",
    "all_ipv4_addresses",
    "all_ipv6_addresses",
    "apparmor",
    "architecture",
    "caps",
    "chroot",
    "cmdline",
    "date_time",
    "default_ipv4",
    "default_ipv6",
    "devices",
    "distribution",
    "distribution_major_version",
    "distribution_release",
    "distribution_version",
    "dns",
    "effective_group_ids",
    "effective_user_id",
    "env",
    "facter",
    "fips",
    "hardware",
    "interfaces",
    "is_chroot",
    "iscsi",
    "kernel",
    "local",
    "lsb",
    "machine",
    "machine_id",
    "mounts",
    "network",
    "ohai",
    "os_family",
    "pkg_mgr",
    "platform",
    "processor",
    "processor_cores",
    "processor_count",
    "python",
    "python_version",
    "real_user_id",
    "selinux",
    "service_mgr",
    "ssh_host_key_dsa_public",
    "ssh_host_key_ecdsa_public",
    "ssh_host_key_ed25519_public",
    "ssh_host_key_rsa_public",
    "ssh_host_pub_keys",
    "ssh_pub_keys",
    "system",
    "system_capabilities",
    "system_capabilities_enforced",
    "user",
    "user_dir",
    "user_gecos",
    "user_gid",
    "user_id",
    "user_shell",
    "user_uid",
    "virtual",
    "virtualization_role",
    "virtualization_type",
];

const TRANSLATIONS: Record<string, string> = {
    [GATHER_FACTS_KEY]: "Общая информация",
    hardware: "Оборудование",
    os: "ОС",
    hotfix: "Обновления",
    user: "Пользователи",
    group: "Группы",
    path: "Пути",
    cpu: "Процессор",
    gpu: "Видеокарта",
    motherboard: "Материнская плата",
    ram: "Оперативная память",
    disk: "Диск",
    pci: "PCI",
    monitor: "Монитор",
    network: "Сеть",
    device: "Устройство",
    other: "Другое",
    security: "Безопасность",
    local_user: "Лок. пользователь",
    local_group: "Лок. группа",
    encryption: "Шифрование",
    software: "Пакет",
    service: "Служба",
};

const ICONS: Record<string, string> = {
    [GATHER_FACTS_KEY]: "rd-icon-info",
    hardware: "rd-icon-setting-tool",
    os: "rd-icon-monitor",
    hotfix: "rd-icon-sync",
    user: "rd-icon-user",
    group: "rd-icon-user",
    path: "rd-icon-folder",
    cpu: "rd-icon-procesor",
    gpu: "rd-icon-videocarta",
    motherboard: "rd-icon-matplata",
    ram: "rd-icon-memory",
    disk: "rd-icon-drive",
    pci: "rd-icon-connect",
    monitor: "rd-icon-screen",
    network: "rd-icon-ip",
    device: "rd-icon-panel",
    other: "rd-icon-setting-tool",
    security: "rd-icon-lock",
    local_user: "rd-icon-user",
    local_group: "rd-icon-user",
    encryption: "rd-icon-disable",
    software: "rd-icon-file",
};

type AssetTypesFieldProps = {
    name: string;
    value: Record<string, string[]>;
    disabled: boolean;
    required: boolean;
    onChange: (name: string, value: Record<string, string[]>) => void;
};

const cleanSelection = (data: Record<string, string[]>): Record<string, string[]> =>
    Object.fromEntries(Object.entries(data).filter(([, arr]) => arr.length > 0));

export default function AssetTypesField({
                                            name,
                                            value,
                                            disabled,
                                            onChange,
                                        }: AssetTypesFieldProps) {
    const [inventoryAssetsTypes, setInventoryAssetsTypes] = useState<Record<string, string[]>>({});
    const [selectedVars, setSelectedVars] = useState<Record<string, string[]>>(value || {});
    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    const [rightMaxHeight, setRightMaxHeight] = useState<number | null>(null);
    const leftRef = useRef<HTMLUListElement>(null);

    useEffect(() => {
        if (Object.keys(inventoryAssetsTypes).length > 0) return;

        apiGet("/inventory/api/inventory-types/")
            .then((data: any) => {
                const merged: Record<string, string[]> = {
                    [GATHER_FACTS_KEY]: GATHER_FACTS_OPTIONS,
                    ...data,
                };
                setInventoryAssetsTypes(merged);

                if (!value || Object.keys(value).length === 0) {
                    setSelectedVars(merged);
                    onChange(name, merged);
                } else {
                    const cleaned = cleanSelection(value);
                    setSelectedVars(cleaned);
                    if (Object.keys(cleaned).length !== Object.keys(value).length) {
                        onChange(name, cleaned);
                    }
                }

                if (!activeCategory) {
                    const first = Object.keys(merged)[0];
                    setActiveCategory(first || null);
                }
            })
            .catch(() => {
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, value, onChange, inventoryAssetsTypes]);

    useEffect(() => {
        if (leftRef.current) {
            setRightMaxHeight(leftRef.current.offsetHeight);
        }
    }, [inventoryAssetsTypes, selectedVars]);

    const categories = useMemo(
        () => Object.keys(inventoryAssetsTypes),
        [inventoryAssetsTypes]
    );

    const allCategoriesSelected = useMemo(
        () =>
            categories.length > 0 &&
            categories.every(
                (cat) =>
                    (selectedVars[cat] || []).length ===
                    (inventoryAssetsTypes[cat] || []).length
            ),
        [categories, inventoryAssetsTypes, selectedVars]
    );

    const activeCategoryItems = useMemo(
        () => (activeCategory ? inventoryAssetsTypes[activeCategory] || [] : []),
        [activeCategory, inventoryAssetsTypes]
    );

    const isActiveCategoryAllSelected = useMemo(() => {
        if (!activeCategory) return false;
        const allItems = inventoryAssetsTypes[activeCategory] || [];
        return (selectedVars[activeCategory] || []).length === allItems.length;
    }, [activeCategory, inventoryAssetsTypes, selectedVars]);

    const updateSelection = (newSelected: Record<string, string[]>) => {
        const cleaned = cleanSelection(newSelected);
        setSelectedVars(cleaned);
        console.log(cleaned)
        onChange(name, cleaned);
    };

    const toggleCheckbox = (category: string, item: string) => {
        const current = selectedVars[category] || [];
        const updated = current.includes(item)
            ? current.filter((v) => v !== item)
            : [...current, item];

        updateSelection({
            ...selectedVars,
            [category]: updated,
        });
    };

    const handleToggleAllCategories = () => {
        const newSelected = allCategoriesSelected
            ? {}
            : Object.fromEntries(
                Object.entries(inventoryAssetsTypes).map(([k, v]) => [k, [...v]])
            );

        updateSelection(newSelected);
    };

    const handleToggleActiveCategory = () => {
        if (!activeCategory) return;

        const allItems = inventoryAssetsTypes[activeCategory] || [];
        const updated = isActiveCategoryAllSelected ? [] : [...allItems];

        updateSelection({
            ...selectedVars,
            [activeCategory]: updated,
        });
    };

    const renderIcon = (key: string) => {
        const cls = ICONS[key] || "rd-icon-question";
        return <span className={`${cls} rd-asset-types__cat-icon`} aria-hidden="true" />;
    };

    return (
        <div className="rd-asset-types">
            <div className="rd-asset-types__col rd-asset-types__col--left">
                <div className="rd-asset-types__header">
                    <span className="rd-asset-types__title">Категории</span>
                    <button
                        type="button"
                        className="rd-btn rd-btn--outline rd-btn--primary rd-btn--small"
                        onClick={handleToggleAllCategories}
                        disabled={disabled}
                    >
                        {allCategoriesSelected ? "Выключить все" : "Выбрать все"}
                    </button>
                </div>

                <ul ref={leftRef} className="rd-asset-types__list">
                    {categories.map((category) => {
                        const selectedItems = selectedVars[category] || [];
                        const displayedItems = selectedItems.slice(0, 2);
                        const extraCount = selectedItems.length - displayedItems.length;
                        const fullTitle = selectedItems
                            .map((item) => TRANSLATIONS[item] || item)
                            .join(", ");
                        const isActive = activeCategory === category;

                        return (
                            <li
                                key={category}
                                onClick={() => setActiveCategory(category)}
                                className={`rd-asset-types__cat-item${isActive ? " rd-asset-types__cat-item--active" : ""}`}
                                title={selectedItems.length > 0 ? fullTitle : undefined}
                            >
                                <div className="rd-asset-types__cat-row">
                                    <div className="rd-asset-types__cat-name-wrap">
                                        {renderIcon(category)}
                                        <span className="rd-asset-types__cat-name">
                                            {TRANSLATIONS[category] || category}
                                        </span>
                                    </div>
                                    {selectedItems.length > 0 && (
                                        <div className="rd-asset-types__cat-badges">
                                            {displayedItems.map((item) => (
                                                <span
                                                    key={item}
                                                    className={`rd-badge rd-asset-types__badge ${
                                                        isActive ? "rd-badge--neutral" : "rd-badge--danger"
                                                    }`}
                                                >
                                                    {TRANSLATIONS[item] || item}
                                                </span>
                                            ))}
                                            {extraCount > 0 && (
                                                <span className="rd-asset-types__cat-extra">
                                                    +{extraCount}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {isActive && <div className="rd-asset-types__active-bar" />}
                            </li>
                        );
                    })}
                </ul>
            </div>

            <div className="rd-asset-types__col rd-asset-types__col--right">
                {activeCategory ? (
                    <>
                        <div className="rd-asset-types__header rd-asset-types__header--right">
                            <button
                                type="button"
                                className="rd-btn rd-btn--outline rd-btn--primary rd-btn--small"
                                onClick={handleToggleActiveCategory}
                                disabled={disabled}
                            >
                                {isActiveCategoryAllSelected ? "Выключить все" : "Выбрать все"}
                            </button>
                        </div>

                        <div
                            className="rd-asset-types__items-wrap"
                            style={{ maxHeight: rightMaxHeight ?? "auto" }}
                        >
                            <ul className="rd-asset-types__items-list">
                                {activeCategoryItems.map((item) => {
                                    const showIcon = activeCategory !== GATHER_FACTS_KEY;
                                    const labelText = showIcon
                                        ? TRANSLATIONS[item] || item
                                        : item;
                                    const iconCls = ICONS[item] || "rd-icon-question";
                                    return (
                                        <li key={item} className="rd-asset-types__item">
                                            <div className="rd-asset-types__item-body">
                                                <CheckboxField
                                                    name={`${activeCategory}-${item}`}
                                                    checked={
                                                        selectedVars[activeCategory!]?.includes(item) || false
                                                    }
                                                    disabled={disabled}
                                                    label={
                                                        showIcon ? (
                                                            <span className="rd-asset-types__item-label">
                                                                <span
                                                                    className={`${iconCls} rd-asset-types__item-icon`}
                                                                    aria-hidden="true"
                                                                />
                                                                {labelText}
                                                            </span>
                                                        ) : (
                                                            labelText
                                                        )
                                                    }
                                                    onChange={() => toggleCheckbox(activeCategory, item)}
                                                />
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </>
                ) : (
                    <div className="rd-asset-types__empty">Выберите категорию слева</div>
                )}
            </div>
        </div>
    );
}
